// Client error reporting (migration 062). Uncaught errors and unhandled
// promise rejections go to log_client_error; admins read them, grouped, at
// /admin/errors. Before this, nothing reported prod errors at all.
//
// What is sent is kept thin on purpose: the message, the stack, the PATH only
// (never the query or hash: reset links and ?code= carry secrets there), the
// platform and the user agent. Emails, URL query strings and JWT-looking
// strings are scrubbed from the message and stack first.
//
// ⚠️ Never pass text a member wrote to reportError(). The Rant Room and the
// Three Good Things journal promise their text never leaves the device.
//
// Production builds only (staging builds count), so local dev and tests send
// nothing. Never throws: a broken reporter must not cause the error it reports.

import { Capacitor } from '@capacitor/core';
import { supabase } from './supabase';

// Mirror 062's caps. The server RAISES over them; truncating here keeps a
// long stack from losing the whole report.
export const CLIENT_ERROR_MESSAGE_MAX = 500;
export const CLIENT_ERROR_STACK_MAX = 4000;
export const CLIENT_ERROR_PATH_MAX = 300;
export const CLIENT_ERROR_UA_MAX = 300;

const SESSION_CAP = 25;          // per page load
const REPEAT_WINDOW_MS = 60000;  // same message again within a minute: skip

const IGNORE = [
  /ResizeObserver loop/i,        // benign browser warning
  /^Script error\.?$/i,          // cross-origin script, no detail to act on
];
const EXTENSION = /(chrome|moz|safari(-web)?)-extension:\/\//i;

let sent = 0;
const lastSeen = new Map();

export function scrub(text) {
  if (!text) return '';
  return String(text)
    .replace(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi, '[email]')
    .replace(/eyJ[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+/g, '[jwt]')
    .replace(/(https?:\/\/[^\s?#)'"]+)[?#][^\s)'"]*/g, '$1?…')
    .replace(/\b(token|code|access_token|refresh_token)=[^\s&)'"]+/gi, '$1=…');
}

const clip = (s, max) => (s && s.length > max ? s.slice(0, max - 1) + '…' : s);

export function buildReport(kind, error, context) {
  const raw = error instanceof Error ? error.message
    : (error && typeof error.message === 'string') ? error.message
    : String(error ?? '');
  const prefix = context ? `${context}: ` : '';
  const message = clip(scrub(`${prefix}${raw}`).trim(), CLIENT_ERROR_MESSAGE_MAX);
  const stack = error && typeof error.stack === 'string'
    ? clip(scrub(error.stack), CLIENT_ERROR_STACK_MAX) : null;
  const path = typeof window !== 'undefined'
    ? clip(window.location.pathname || '/', CLIENT_ERROR_PATH_MAX) : null;
  const ua = typeof navigator !== 'undefined'
    ? clip(navigator.userAgent || '', CLIENT_ERROR_UA_MAX) : null;
  let platform = 'web';
  try { platform = Capacitor.getPlatform(); } catch { /* web */ }
  if (!['web', 'ios', 'android'].includes(platform)) platform = 'web';
  return { p_kind: kind, p_message: message, p_stack: stack, p_path: path, p_platform: platform, p_user_agent: ua || null };
}

function shouldSend(report) {
  if (!report.p_message) return false;
  if (IGNORE.some((re) => re.test(report.p_message))) return false;
  if (report.p_stack && EXTENSION.test(report.p_stack)) return false;
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return false;
  if (sent >= SESSION_CAP) return false;
  const key = `${report.p_kind}|${report.p_message}`;
  const now = Date.now();
  if (now - (lastSeen.get(key) || 0) < REPEAT_WINDOW_MS) return false;
  lastSeen.set(key, now);
  return true;
}

/**
 * Report an error. kind: 'error' (uncaught), 'promise' (unhandled rejection)
 * or 'api' (a call that failed in a way we want to see, e.g. sign-up).
 * context is a short label prefixed to the message, like 'signUp'.
 */
export function reportError(error, kind = 'api', context) {
  if (process.env.NODE_ENV !== 'production') return;
  try {
    const report = buildReport(kind, error, context);
    if (!shouldSend(report)) return;
    sent += 1;
    supabase.rpc('log_client_error', report).then(() => {}, () => {});
  } catch {
    // never let reporting throw
  }
}

let installed = false;

export function installErrorReporting() {
  if (installed || typeof window === 'undefined') return;
  installed = true;
  window.addEventListener('error', (event) => {
    reportError(event.error || event.message, 'error');
  });
  window.addEventListener('unhandledrejection', (event) => {
    reportError(event.reason, 'promise');
  });
}
